import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Wand2, Loader2 } from "lucide-react";

interface InsertedPick {
  id: string;
  title: string;
  edge: string;
  book: string;
}

interface RunResult {
  inserted: InsertedPick[];
  totalCandidates: number;
  sportsScanned: { sportKey: string; games: number; error: string | null }[];
  apiCalls: number;
  quota: { remaining: string | null; used: string | null };
  errors: string[];
}

export default function AutoTop5Generator() {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<RunResult | null>(null);

  async function run() {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("auto_top5_picks", { body: {} });
      if (error) throw error;
      setLast(data as RunResult);
      const n = data?.inserted?.length ?? 0;
      if (n === 0) toast.warning("No positive-edge picks found — try again later.");
      else toast.success(`Created ${n} draft picks. Review them below.`);
      window.dispatchEvent(new Event("picks:refresh"));
    } catch (e: any) {
      toast.error(e.message ?? "Auto-scan failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Wand2 className="w-5 h-5 text-accent" />
          Auto Top 5 Generator
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Scans NBA, NFL, MLB, NHL, EPL, UCL, MLS for moneyline / spread / total value vs market consensus across US books, then drops the best 5 in as <strong>drafts</strong> for your approval. Nothing is published automatically.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={busy} size="lg">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
          {busy ? "Scanning books…" : "Generate Today's Top 5"}
        </Button>

        {last && (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-3 text-muted-foreground">
              <span>API calls: <strong className="text-foreground">{last.apiCalls}</strong></span>
              <span>Candidates: <strong className="text-foreground">{last.totalCandidates}</strong></span>
              <span>Quota remaining: <strong className="text-foreground">{last.quota?.remaining ?? "—"}</strong></span>
            </div>

            <div>
              <div className="font-medium mb-1">Sports scanned</div>
              <div className="flex flex-wrap gap-2">
                {last.sportsScanned.map((s) => (
                  <span key={s.sportKey} className={`te-chip text-xs border ${s.error ? "border-destructive text-destructive" : "border-border"}`}>
                    {s.sportKey}: {s.error ? `error` : `${s.games} games`}
                  </span>
                ))}
              </div>
            </div>

            {last.inserted.length > 0 && (
              <div>
                <div className="font-medium mb-1">Drafts created</div>
                <ul className="space-y-1">
                  {last.inserted.map((p) => (
                    <li key={p.id} className="flex items-center justify-between border border-border rounded px-2 py-1">
                      <span>{p.title}</span>
                      <span className="text-muted-foreground text-xs">edge {p.edge}% · {p.book}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {last.errors.length > 0 && (
              <div className="text-destructive">
                <div className="font-medium">Errors</div>
                <ul className="list-disc pl-5">{last.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
